import { useMemo } from "react";

import { __GLOBAL_TENANT } from "@/client/globals";
import { API_ENDPOINT } from "@/config";
import { FeedbackTabId } from "@/features/feedback/components/types";
import { MarkingTool, SubmissionType } from "@/generated/graphql";

const HERA_API_URL = `${API_ENDPOINT}/api/hera`;

interface Props {
  workId: string;
  markingTool: MarkingTool;
  submissionType: SubmissionType;
  isReady: boolean;
  component?: FeedbackTabId;
  trackGradebookAccess?: boolean;
}

/**
 * Returns a function to launch the given marking tool in a new tab. 
 */
export function useMarkingToolLaunch(props: Props): VoidFunction | undefined {
  const {
    workId,
    markingTool,
    submissionType,
    isReady,
    component,
    trackGradebookAccess,
  } = props;
  return useMemo(() => {
    let endpoint = "result";

    if (markingTool === MarkingTool.Speedgrader) {
      endpoint = "speedgrader";
    } else if (markingTool === MarkingTool.Moodlegrader) {
      endpoint = "moodlegrader";
    } else if (markingTool === MarkingTool.Cadmusgrader) {
      endpoint = "cadmusgrader";
    } else if (markingTool === MarkingTool.Turnitin) {
      endpoint = "result";
    }

    if (isReady) {
      const url = createGraderURL(
        workId,
        endpoint,
        submissionType,
        trackGradebookAccess ?? true,
        component
      );
      return () => {
        const win = window.open(url.href, "_blank");
        if (win) {
          win.focus();
        }
      };
    }
    return undefined;
  }, [
    workId,
    isReady,
    markingTool,
    component,
    submissionType,
    trackGradebookAccess,
  ]);
}

const createGraderURL = (
  workId: string,
  endpoint: string,
  submissionType: SubmissionType,
  trackGradebookAccess: boolean,
  component?: FeedbackTabId
) => {
  const url = new URL(`${HERA_API_URL}/${endpoint}`);
  url.searchParams.append("role", "student");
  url.searchParams.append("work_id", workId);
  if (submissionType === SubmissionType.Draft) {
    url.searchParams.append("submission_type", "draft");
  }
  // Ignore status and rely on GroupFeedbackPage useEffect to record gradebook access.
  if (!trackGradebookAccess) url.searchParams.append("track_status", "ignore");
  if (component) url.searchParams.append("component", toString(component));
  if (__GLOBAL_TENANT.current) {
    url.searchParams.append("tenant", __GLOBAL_TENANT.current);
  }
  return url;
};

const toString = (tab: FeedbackTabId): string => {
  switch (tab) {
    case FeedbackTabId.IndividualReport:
      return "individual";
    case FeedbackTabId.GroupReport:
      return "group";
    case FeedbackTabId.PeerEvaluation:
      return "peer";
    default:
      return "group";
  }
};
